import { captureException, setUser as setSentryUser } from "@sentry/nextjs";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useSWR from "swr";

import useCurrentOrganisation from "hooks/useCurrentOrganisation";
import useDirectAuth from "hooks/useDirectAuth";
import useWindowFocus from "hooks/useWindowFocus";
import useWorkOS from "hooks/useWorkOS";

import I18n from "services/I18n";
import JsonApi from "services/JsonApi";
import { Impersonator } from "services/auth/types";
import { ClientAccount } from "services/client_accounts/types";
import { organisationPath } from "services/organisations/paths";
import { Organisation } from "services/organisations/types";
import { initialsFromName } from "services/users";
import { userPath } from "services/users/paths";
import { User } from "services/users/types";

interface AuthContextUser {
  id?: string;
  email?: string;
  organisationId?: number;
  roles?: string[];
  name?: string;
  type?: string;
  initials?: string;
  avatar_url?: string | null;
}

export interface AuthProviderProps {
  children: ReactNode;
}

interface SignOutProps {
  redirectTo?: string | null;
}

export interface AuthContextProps {
  signOut: (options?: SignOutProps) => Promise<any>;
  getAccessToken: () => Promise<string>;
  isLoading: boolean;
  user?: AuthContextUser;
  authError?: string;
  setAuthError: Dispatch<SetStateAction<string>>;
  startAuthFlow: (
    email: string
  ) => Promise<{ authorisation_url?: string } | undefined>;
  verifyCode: (email: string, code: string) => Promise<any>;
  setCurrentOrganisationId: (org: string) => void;
  currentOrganisationId?: string;
  accessToken: string;
  organisation?: Organisation;
  clientAccount?: ClientAccount;
  initializationError: string | null;
  initializationFailed: string | null;
  impersonator: Impersonator | null;
  roles: string[];
  refreshUser: () => void;
}

const defaultContextValue = {
  getAccessToken: async () => {
    console.warn("Auth not initialised yet");
    return "";
  },
  signOut: async () => {
    console.warn("Auth not initialised yet");
  },
  isLoading: true,
  accessToken: "",
  user: undefined,
  directUser: undefined,
  passwordlessUser: undefined,
  auth0User: undefined,
  setAuthError: () => {
    console.warn("Auth not initialised yet");
  },
  passwordless: undefined,
  startAuthFlow: async () => ({}),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  verifyCode: async () => {},
  setCurrentOrganisationId: () => {
    console.warn("Auth not initialised yet");
  },
  currentOrganisationId: undefined,
  roles: [],
  refreshUser: () => {
    console.warn("Auth not initialised yet");
  },
  initializationError: null,
  initializationFailed: null,
  impersonator: null,
};

export const AuthContext = createContext<AuthContextProps>(defaultContextValue);

export const getUserIdAndOrg = (user?: any) => {
  if (!user) {
    return null;
  }

  const isGarageToken = user.organisation_id != null;
  const userId: number =
    user["urn:residently:user_id"] ?? (isGarageToken ? user.sub : undefined);
  const organisationId: number =
    user["urn:residently:organisation_id"] ?? user.organisation_id;

  if (!userId || !organisationId) {
    return null;
  }

  return {
    id: userId.toString(),
    organisationId,
  };
};

const RETURN_TO_KEY = "loginReturnTo";

const setReturnTo = (val: string | null) => {
  if (val) {
    sessionStorage.setItem(RETURN_TO_KEY, val);
  } else {
    sessionStorage.removeItem(RETURN_TO_KEY);
  }
};

const getReturnTo = () => sessionStorage.getItem(RETURN_TO_KEY) || null;

const authSessionErrors = ["no_session", "login_required"];

const MAX_INIT_RETRIES = 5;

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [authError, setAuthError] = useState("");
  const focused = useWindowFocus();
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

  const {
    isLoading: directAuthLoading,
    token: directToken,
    user: directUser,
    clearDirectToken,
  } = useDirectAuth();

  const workOSEnabled = !directAuthLoading && !directToken;

  const {
    error: workOSError,
    user: workOSUser,
    isLoading: workOSAuthLoading,
    startAuthFlow: workOSStartAuthFlow,
    verifyCode: workOSVerifyCode,
    signOut: workOSSignOut,
    accessToken: workOSAccessToken,
    checkSession: workOSCheckSession,
    impersonator: workOSImpersonator,
  } = useWorkOS({ enabled: workOSEnabled });

  const pathIsALoginPage = pathname?.startsWith("/login");
  const pathIsLoginComplete = pathname === "/login/complete";

  const userOrganisationId = useMemo(() => {
    const authUser = directUser || workOSUser;
    if (!authUser) {
      return undefined;
    }

    return getUserIdAndOrg(authUser)?.organisationId;
  }, [directUser, workOSUser]);

  const userIdFromClaim = useMemo(() => {
    const authUser = directUser || workOSUser;
    return getUserIdAndOrg(authUser)?.id;
  }, [directUser, workOSUser]);

  const {
    currentOrganisationId,
    setCurrentOrganisationId,
    clearOrganisationId,
    isLoading: orgIdLoading,
  } = useCurrentOrganisation(userOrganisationId, userIdFromClaim);

  const authLoading = workOSAuthLoading || directAuthLoading || orgIdLoading;

  // Once either SWR fetch exhausts its retries, `initializationFailedState`
  // latches to true. SWR won't retry again (revalidateOnFocus is false), so
  // the only way back is a page refresh — keep this as a one-way flag.
  const [initializationFailedState, setInitializationFailedState] =
    useState(false);

  useEffect(() => {
    // If the workOS accessToken changes, update our copy of the accessToken
    if (workOSAccessToken) {
      setAccessToken(workOSAccessToken);
    }
  }, [workOSAccessToken]);

  useEffect(() => {
    if (directToken) {
      setAccessToken(directToken);
    } else {
      setAccessToken(undefined);
    }
  }, [directToken]);

  const sendToLogin = useMemo(
    () => () => {
      if (authLoading) {
        return;
      }

      if (!authError) {
        setAuthError("Login to continue");
      }

      if (!pathIsALoginPage) {
        const queryParams = searchParams
          ? new URLSearchParams(Array.from(searchParams.entries())).toString()
          : null;
        const newReturnTo = `${pathname}${queryParams ? `?${queryParams}` : ""}`;
        setReturnTo(newReturnTo);
      }

      router.push("/login");
    },
    [authLoading, authError, pathIsALoginPage, router, searchParams, pathname]
  );

  useEffect(() => {
    const errorParam = searchParams?.get("error");
    if (!errorParam) {
      return;
    }

    setAuthError(I18n.t("login.errors.sso_failed", { error: errorParam }));

    if (!pathIsALoginPage || pathIsLoginComplete) {
      router.push("/login");
    }
  }, [searchParams]);

  const getAccessToken = useCallback(async () => {
    if (directToken) {
      return directToken;
    }

    if (authLoading) {
      return "";
    }

    try {
      return await workOSCheckSession();
    } catch (err: any) {
      if (authSessionErrors.includes(err.error)) {
        // redirect to login?
      } else {
        captureException(err);
      }
    }

    return "";
  }, [directToken, authLoading, workOSCheckSession]);

  // We have to load the organisation here with a direct call to useSWR
  // using a hook creates a ciruclar dependency between the AuthContext and the useOrganisations
  // the hook never executes in that scenario and the organisation is never loaded.
  const { data: organisation, error: organisationError } = useSWR(
    !authLoading && accessToken && userIdFromClaim && currentOrganisationId
      ? `${organisationPath(currentOrganisationId)}:org-context`
      : null,
    async () => {
      const token = await getAccessToken();
      const api = new JsonApi({ accessToken: token, currentOrganisationId });
      const { data } = await api.get(
        organisationPath(currentOrganisationId as string) as string,
        {
          params: {
            include: ["client_account"],
            fields: {
              organisations: ["id", "internal_name", "client_account"],
              client_accounts: ["id", "mass_communications_enabled"],
            },
          },
        }
      );
      return data;
    },
    {
      revalidateOnFocus: false,
      onErrorRetry: (_err, _key, _config, revalidate, { retryCount }) => {
        if (retryCount >= MAX_INIT_RETRIES) {
          setInitializationFailedState(true);
          return;
        }

        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    }
  );

  const clientAccount = organisation?.client_account;

  const {
    data: currentUser,
    error: currentUserError,
    mutate: refreshUser,
  } = useSWR<User>(
    !authLoading && accessToken && userIdFromClaim
      ? `${userPath(userIdFromClaim)}:auth-context`
      : null,
    async () => {
      const token = await getAccessToken();
      const api = new JsonApi({ accessToken: token });
      const { data } = await api.get(
        userPath(userIdFromClaim as string) as string,
        {
          params: {
            fields: {
              users: [
                "id",
                "email",
                "name",
                "client_account_role",
                "roles",
                "avatar_url",
              ],
            },
          },
        }
      );
      return data as User;
    },
    {
      revalidateOnFocus: false,
      onErrorRetry: (_err, _key, _config, revalidate, { retryCount }) => {
        if (retryCount >= MAX_INIT_RETRIES) {
          setInitializationFailedState(true);
          return;
        }

        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    }
  );

  const isLoading = authLoading || (Boolean(userIdFromClaim) && !currentUser);

  const roles = useMemo<string[]>(
    () => currentUser?.roles ?? [],
    [currentUser?.roles]
  );

  const user = useMemo<AuthContextUser | undefined>(() => {
    if (isLoading) {
      return undefined;
    }

    const authUser = directUser || workOSUser;
    const claim = getUserIdAndOrg(authUser);

    if (!claim || !authUser) {
      return undefined;
    }

    const email = currentUser?.email ?? authUser.email;
    const name = currentUser?.name ?? authUser.name;

    return {
      id: currentUser?.id ?? claim.id,
      organisationId: claim.organisationId,
      email,
      name,
      initials: initialsFromName(name),
      avatar_url: currentUser?.avatar_url ?? null,
      roles,
      type: directUser ? "direct" : "passwordless",
    };
  }, [isLoading, directUser, workOSUser, currentUser, roles]);

  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
    }
  }, [user, isLoading]);

  useEffect(() => {
    if (isLoading || directUser) {
      return;
    }

    const alreadyAuthenticated = !isLoading && user;
    const notAuthenticated = !isLoading && !user;
    const pathIsLoginJourney =
      pathname?.startsWith("/login") && !pathIsLoginComplete;
    const returnTo = getReturnTo();

    if (alreadyAuthenticated && pathIsALoginPage) {
      setReturnTo(null);
      router.push(returnTo || "/dashboard");
    } else if (
      workOSError &&
      authSessionErrors.includes(workOSError) &&
      !pathIsLoginJourney
    ) {
      sendToLogin();
    } else if (notAuthenticated && !pathIsLoginJourney) {
      sendToLogin();
    }
  }, [isLoading, user, directUser, pathname, workOSError]);

  const signOut = useCallback(
    async ({ redirectTo = "/login" }: SignOutProps = {}) => {
      clearOrganisationId();

      const url = new URL(window.location.href);
      url.pathname = redirectTo || "/login";
      url.search = "";

      workOSSignOut();

      clearDirectToken();
    },
    [clearOrganisationId, workOSSignOut, clearDirectToken]
  );

  const startAuthFlow = useCallback(
    async (email: string) => {
      setAuthError("");
      try {
        return await workOSStartAuthFlow(email);
      } catch (err) {
        setAuthError(err.message);
      }
    },
    [workOSStartAuthFlow]
  );

  const verifyCode = useCallback(
    async (email: string, code: string) => {
      await workOSVerifyCode(email, code);
    },
    [workOSVerifyCode]
  );

  useEffect(() => {
    // This will trigger a refresh of the access token if the page becomes focused
    if (!focused) {
      return;
    }

    const fetchToken = async () => {
      const token = await getAccessToken();
      setAccessToken(token);
    };

    fetchToken();
  }, [focused, getAccessToken]);

  const initializationError = useMemo(() => {
    if (initializationFailedState) {
      return null;
    }

    if (currentUserError || organisationError) {
      return I18n.t("auth.initialization.retrying");
    }

    return null;
  }, [initializationFailedState, currentUserError, organisationError]);

  const initializationFailed = initializationFailedState
    ? I18n.t("auth.initialization.failed")
    : null;

  const value = useMemo(
    () => ({
      getAccessToken,
      signOut,
      isLoading,
      user,
      directUser,
      authError,
      setAuthError,
      startAuthFlow,
      verifyCode,
      setCurrentOrganisationId,
      currentOrganisationId,
      accessToken: accessToken || "",
      organisation,
      clientAccount,
      initializationError,
      initializationFailed,
      impersonator: workOSImpersonator,
      roles,
      refreshUser,
    }),
    [
      getAccessToken,
      signOut,
      isLoading,
      user,
      directUser,
      authError,
      startAuthFlow,
      verifyCode,
      setCurrentOrganisationId,
      currentOrganisationId,
      accessToken,
      organisation,
      clientAccount,
      initializationError,
      initializationFailed,
      workOSImpersonator,
      roles,
      refreshUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
